import { Link } from 'react-router-dom';
import { Ticket, ExternalLink } from 'lucide-react';

// Oct 2026 — SPAC right explainer. Standalone card for right tickers
// (HAVAR…) and compact variant embedded in UnitPanel when the unit
// bundles a right. Ratio parsed from the Massive name when present
// ("Rights that convert on a 1/10th of 1 basis").
export function parseRightRatio(name = '') {
  const m = name.match(/(\d+)\s*\/\s*(\d+)(?:th|st|nd|rd)?/i);
  if (m) return `${m[1]}/${m[2]}`;
  const words = { 'one-tenth': '1/10', 'one-eighth': '1/8', 'one-fifth': '1/5', 'one-twentieth': '1/20', 'one-fifteenth': '1/15' };
  const w = Object.keys(words).find((k) => name.toLowerCase().includes(k));
  return w ? words[w] : null;
}

export function isSpacRight(stock) {
  if (!stock) return false;
  if (stock.non_dividend_security_class === 'right' || stock.non_dividend_reason === 'structural_right') return true;
  const n = stock.long_name || stock.name || '';
  return /\bRights?\b/i.test(n) && (stock.is_spac === true || /acquisition|capital corp|blank check/i.test(n));
}

export function rightName(stock) {
  return stock?.long_name || stock?.short_name || stock?.name || '';
}

export function rightShareTicker(ticker = '') {
  const m = ticker.match(/^([A-Z]{3,4})R$/);
  return m ? m[1] : null;
}

// Links that leave the Stock Modal must go through navigateWithOrigin so
// the browser Back button reopens the modal on the same tab.
export function GuideLink({ to, onNavigate, className, testId, children }) {
  if (onNavigate) {
    return (
      <button type="button" onClick={() => onNavigate(to)} className={className} data-testid={testId}>{children}</button>
    );
  }
  return <Link to={to} className={className} data-testid={testId}>{children}</Link>;
}

export function RightBadge({ testIdSuffix }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-fuchsia-500/20 text-fuchsia-800 dark:text-fuchsia-300 ring-1 ring-fuchsia-500/50"
      title="SPAC right — converts automatically into a fraction of a share (commonly 1/10) when the merger closes. Worthless if the SPAC liquidates. Pays NO dividends."
      data-testid={testIdSuffix ? `right-badge-${testIdSuffix}` : 'right-badge'}
    >
      <Ticket className="w-2.5 h-2.5" />
      <span>RIGHT</span>
    </span>
  );
}

export default function RightPanel({ ticker, name, cik, shareTicker, ratio, compact = false, onOpenTicker = null, onNavigate = null }) {
  const r = ratio || parseRightRatio(name || '');
  const edgarUrl = cik ? `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${cik}&type=8-K` : null;
  const share = shareTicker ? (
    onOpenTicker ? (
      <button type="button" onClick={() => onOpenTicker(shareTicker)} className="font-mono font-bold text-violet-700 dark:text-violet-300 hover:underline" data-testid="right-panel-share-link">{shareTicker}</button>
    ) : <span className="font-mono font-bold">{shareTicker}</span>
  ) : 'the Class A share';

  return (
    <div className={`rounded-xl border border-fuchsia-400/50 bg-fuchsia-50/40 dark:bg-fuchsia-950/20 ${compact ? 'p-3 space-y-2' : 'p-4 space-y-3'}`} data-testid="stock-modal-right-panel">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Ticket className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300" />
          <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">{compact ? 'The right inside this unit' : 'SPAC Right'}</h4>
          <RightBadge testIdSuffix={compact ? 'unit-panel' : 'right-panel'} />
        </div>
        {!compact && edgarUrl && (
          <a href={edgarUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-mono text-cyan-700 dark:text-cyan-300 hover:underline" data-testid="right-panel-cik-link">
            CIK {cik} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {!compact && name && <div className="text-xs text-muted-foreground">{name}</div>}
      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
        {ticker ? <b>{ticker}</b> : 'A right'} is <b>not</b> a share and <b>not</b> a warrant. It is a promise: when the SPAC closes its merger, each right
        converts <b>automatically</b> into <b>{r ? `${r} of a share` : 'a fraction of a share (1/10 is the most common)'}</b> of {share}. There is nothing to
        exercise and nothing to pay — your broker swaps the rights for shares on the closing date.
      </p>
      <div className={`grid ${compact ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'} gap-2 text-xs`}>
        <div className="rounded-md border border-fuchsia-500/30 bg-white/60 dark:bg-fuchsia-950/30 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300">Merger closes</div>
          <div className="text-slate-700 dark:text-slate-200 leading-snug">{r ? `${r} share per right` : 'Fraction of a share per right'} — e.g. 10 rights at 1/10 become 1 share. Fractions are usually rounded down or paid in cash.</div>
        </div>
        <div className="rounded-md border border-fuchsia-500/30 bg-white/60 dark:bg-fuchsia-950/30 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300">SPAC liquidates</div>
          <div className="text-slate-700 dark:text-slate-200 leading-snug">Rights expire <b>worthless</b>. Only the share gets the ~$10 trust cash back — rights and warrants get nothing.</div>
        </div>
        <div className="rounded-md border border-fuchsia-500/30 bg-white/60 dark:bg-fuchsia-950/30 p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300">Fair value</div>
          <div className="text-slate-700 dark:text-slate-200 leading-snug">≈ share price × {r || '1/10'} × odds the deal closes. A right at $0.60 with a $10 share prices roughly a 60% chance of closing.</div>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Rights pay no dividends and carry no vote. Deal details, the exact ratio and the closing date live in the issuer's 8-K / S-4 filings.
        {' '}
        <GuideLink to="/learn/units" onNavigate={onNavigate} className="font-semibold text-fuchsia-700 dark:text-fuchsia-300 hover:underline" testId="right-panel-learn-link">Units, warrants &amp; rights guide →</GuideLink>
      </p>
    </div>
  );
}
